import React from 'react';
import { noop } from 'lodash';

import { DEFAULT_COLOR, DEFAULT_PURPLE_COLOR, HEXColor } from '@neptune/colors-domain';
import { useRunsColorsContext } from '@neptune/compare-business-logic';
import { useLineageEntityTypeContext } from '@neptune/shared/entity-business-logic';
import { getRunIdentificationPair } from '@neptune/shared/entity-domain';

import { Attribute } from 'domain/experiment/attribute';

type ItemColor = HEXColor | undefined;
type Item = {
  attributes: Attribute[];
  organizationName: string;
  projectName: string;
  projectIdentifier: string;
};

/**
 *
 * Returns a list of functions that can be used to get the color of an entity.
 * When used with *ColorPaletteContext* functions with lower index have higher priority
 *
 */
export function useRunDetailsColorsSources(): [
  (((item: Item) => ItemColor | undefined) | undefined)[],
  { getColorForGroup: (tag: string) => ItemColor },
] {
  const { getColor, makeColorKey } = useRunsColorsContext();
  const { getEntityIdentificationKeyInContextOfLineage } = useLineageEntityTypeContext();

  return React.useMemo(
    () => [
      [
        (item: Item) => {
          const { organizationName, projectName, projectIdentifier } = item;
          const runIdentificationPair = getRunIdentificationPair(item);

          if (!organizationName || !projectName || !runIdentificationPair) {
            return getColor() as ItemColor;
          }

          const runIdentificationKey = getEntityIdentificationKeyInContextOfLineage(
            projectIdentifier,
            runIdentificationPair,
          );

          const color = getColor(
            makeColorKey(organizationName, projectName, runIdentificationKey),
          ) as ItemColor;

          return color === DEFAULT_COLOR ? DEFAULT_PURPLE_COLOR : color;
        },
      ],
      { getColorForGroup: noop as (tag: string) => ItemColor },
    ],
    [getColor, makeColorKey, getEntityIdentificationKeyInContextOfLineage],
  );
}
